<template>
  <div class="combook-binding-confirm">
    <h1 class="title-page">
      <v-icon class="material-icons-outlined">import_contacts</v-icon>
      <br />製本の内容確認
    </h1>
    <v-card class="card">
      <section class="preview-cover">
        <v-window v-model="selected" class="preview-window">
          <v-window-item v-for="(img, i) in images" :key="i" :value="i">
            <v-label>{{ img.label }}</v-label>
            <v-layout justify-center style="padding-top: 24px;">
              <v-flex shrink>
                <div class="thumb">
                  <div
                    class="croper"
                    :style="{
                      width: img.crop_width,
                      ...(img.crop_height ? { height: img.crop_height } : {})
                    }"
                  >
                    <img
                      :src="getImagePath(img)"
                      :style="{
                        ...(img.crop_height
                          ? {
                              height: img.crop_height,
                              objectFit: 'cover',
                              objectPosition: 'right center'
                            }
                          : {})
                      }"
                    />
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-window-item>
        </v-window>

        <v-layout row class="select-thumbnail">
          <v-flex
            v-for="(img, i) in images"
            :key="i"
            xs3
            class="select-thumbnail__item"
            :class="{ 'select-thumbnail__item--active': selected === i }"
          >
            <v-label>{{ img.label }}</v-label>
            <v-layout justify-center style="padding-top: 20px;">
              <v-flex shrink>
                <div class="thumb-mini">
                  <div
                    class="croper-mini"
                    :style="{
                      width: img.thumb_crop_width,
                      ...(img.crop_height ? { height: img.crop_height } : {})
                    }"
                  >
                    <img
                      :src="getImagePath(img)"
                      :style="{
                        ...(img.crop_height
                          ? {
                              height: img.crop_height,
                              objectFit: 'cover',
                              objectPosition: 'right center'
                            }
                          : {})
                      }"
                      class="pointer"
                      @click="selected = i"
                    />
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
        <section class="attention-text">
          ※表紙の園・施設名やお名前が正しく表示されているか必ずご確認ください。
        </section>
      </section>
      <section class="cover-info">
        <div>
          <h3>連絡帳製本</h3>
          {{ price }}円（税込）
        </div>
        <ul>
          <li>園・施設名： {{ organization.name }}</li>
          <li>名前： {{ familyName }} {{ selectedKid.name }}</li>
          <li>期間： {{ periodLabel }}</li>
          <li>表紙デザイン： {{ designTypeLabel }}</li>
          <li>表紙の印字： {{ coverText }}</li>
        </ul>
      </section>
      <hr />
      <section class="cover-spec">
        <h3>詳細仕様</h3>
        <CoverSpecTable />
        <a
          href=""
          class="align-right"
          style="font-weight: 700;"
          @click.prevent="$emit('show-product-detail-full-screen-dialog')"
          >商品詳細</a
        >
      </section>
      <section class="attention-text">
        【必ずご確認ください】<br />
        ・連絡帳製本のデイリーページ連絡欄で利用されている記号や絵文字、環境依存文字は、文字化け・別の文字に置き換わる可能性があります。
        予めご了承ください。詳しい非対応文字は
        <a
          href="https://lookmee.tayori.com/q/lookmee-faq-c/detail/1168352/"
          target="_blank"
          rel="noopener"
          >よくある質問<v-icon small color="primary">open_in_new</v-icon></a
        >をご確認ください。<br />
        ・この製本は、ルクミー連絡帳が対象です。実際に記録された日数を問わず、選択した期間のルクミー連絡帳の記録を印刷します。
        事前にルクミー for FAMILY アプリで記録内容のご確認をお願いします。<br />
        ・製本されるページは、3ヶ月に含まれる全ての日（土日祝日を含む）が対象となります。<br />
        ・残りページはメモページとして印刷されます。<br />
        ・商品の性質上、画面と実物の色味に差異が発生してしまう場合がございます。また、表紙に最大3㎜のズレが生じる可能性があります。予めご了承ください。
      </section>
      <v-layout justify-center column align-center>
        <v-btn class="v-btn--minwidth" color="primary" @click="handleAddToCart">
          <v-icon class="material-icons">shopping_cart</v-icon>
          カートに入れる
        </v-btn>
        <v-btn class="v-btn--minwidth" color="negative" @click="handleBack">
          表紙を作り直す
        </v-btn>
      </v-layout>
    </v-card>

    <v-layout row justify-center>
      <v-dialog v-model="showDialog" max-width="305">
        <v-card class="dialog-card">
          <p class="dialog-content">
            ページを移動すると、作成した表紙を作り直すことになりますが、よろしいですか？
          </p>
          <v-card-actions>
            <v-layout justify-center class="dialog-actions">
              <v-btn color="primary" @click="confirmBack">
                OK
              </v-btn>
              <v-btn color="negative" @click="cancelBack">
                キャンセル
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import CoverSpecTable from "../Tables/CoverSpecTable.vue";
import ApiErrorHandler from "../../../../mixins/ApiErrorHandler";

const DESIGN_TYPE_LABELS = {
  pastel: "パステル",
  simple: "シンプル"
};

export default Vue.extend({
  name: "Confirm",
  components: {
    CoverSpecTable
  },
  mixins: [ApiErrorHandler],
  props: {
    organization: {
      type: Object,
      required: true
    },
    price: {
      type: String,
      required: true
    },
    familyName: {
      type: String,
      required: true
    },
    organizationName: {
      type: String,
      required: true
    },
    kidName: {
      type: String,
      required: true
    },
    selectedKid: {
      type: Object,
      default: null
    },
    selectedPeriod: {
      type: Object,
      default: null
    },
    selectedDesignType: {
      type: String,
      required: true
    },
    combookBindingCover: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selected: 0,
      showDialog: false
    };
  },
  computed: {
    designTypeLabel(): string {
      return DESIGN_TYPE_LABELS[this.selectedDesignType];
    },
    periodLabel(): string {
      return `${this.selectedPeriod.text}`;
    },
    coverText(): string {
      return `${this.organizationName} / ${this.kidName}`;
    },
    images(): {
      src: string;
      label: string;
      crop_width: string;
      thumb_crop_width: string;
      crop_height: string | null;
      isExternal: boolean;
    }[] {
      const coverImageUrl = this.combookBindingCover.thumbnailUrl;
      return [
        {
          src: coverImageUrl,
          label: "表紙デザイン",
          crop_width: "141px",
          crop_height: "100%",
          thumb_crop_width: "34px",
          isExternal: true
        },
        {
          src: coverImageUrl,
          label: "表紙・裏表紙",
          crop_width: "100%",
          crop_height: null,
          thumb_crop_width: "100%",
          isExternal: true
        },
        {
          src: "sample_daily.jpg",
          label: "デイリーページ (サンプル)",
          crop_width: "100%",
          crop_height: null,
          thumb_crop_width: "100%",
          isExternal: false
        },
        {
          src: "sample_memo.jpg",
          label: "メモページ  (サンプル)",
          crop_width: "100%",
          crop_height: null,
          thumb_crop_width: "100%",
          isExternal: false
        }
      ];
    }
  },
  methods: {
    getImagePath(img) {
      if (img.isExternal) {
        return img.src;
      }
      return require(`../../../../assets/images/combook_binding/${img.src}`);
    },

    async addCombookBindingToCart(): Promise<any> {
      return await this.$store.dispatch("cart/addCombookBindingToCart", {
        organizationId: this.organization.id,
        coverId: this.combookBindingCover.id
      });
    },

    async checkCombookBindingCartExists(): Promise<any> {
      // apiClient はルートの state から取得
      const apiClient = this.$store.state.apiClient;
      return await apiClient.existsCombookBindingInCart(this.organization.id);
    },

    async handleAddToCart() {
      this.$emit("ignore-before-route-leave");
      try {
        // 1. 既存カート確認
        const existingCart = await this.checkCombookBindingCartExists();
        if (existingCart) {
          const salesManagementId = existingCart.salesManagementId;
          this.$emit("already-exist-in-cart", salesManagementId);
          return;
        }

        // 2. 無ければ実際に追加
        const cart = await this.addCombookBindingToCart();
        if (!cart) {
          return;
        }

        const salesManagementId = (cart as any).salesManagementId;

        this.$router.push({
          name: "organizationCart",
          params: {
            organizationId: this.organization.id,
            salesManagementId
          }
        });
      } catch (errors) {
        console.error(errors);
        this.handleApiErrors(errors, {
          store: this.$store,
          router: this.$router,
          sentry: this.sentry
        });
      }
    },
    handleBack() {
      this.showDialog = true;
    },
    confirmBack() {
      this.showDialog = false;
      this.$emit("back");
    },
    cancelBack() {
      this.showDialog = false;
    }
  }
});
</script>

<style scoped>
h3 {
  margin-bottom: 4px;
}
hr {
  color: #000000;
  opacity: 0.12;
}
.card {
  max-width: 443px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: #545454;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
}

.preview-window {
  background: #fbfbf9;
  border-radius: 4px;
  border: 1px solid #eaeae8;

  height: 262px;
}

.preview-window >>> .v-label,
.select-thumbnail >>> .v-label {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  gap: 10px;

  width: fit-content;
  top: 0px;

  background: #4cb5b8;
  border-radius: 4px 0px 4px 0px;
  overflow: hidden;
  color: #ffffff;
  font-weight: 700;
  font-size: 12px;
  line-height: 140%;
  position: absolute !important;
  z-index: 1;
}

.preview-window >>> .v-window-item {
  padding: 18px;
}

.preview-cover {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-thumbnail {
  gap: 8px;
}

.select-thumbnail >>> .flex {
  position: relative;
}

.select-thumbnail >>> .v-label {
  font-size: 5px;
  line-height: 100%;
}

.select-thumbnail__item >>> .layout {
  background: #fbfbf9;
  border-radius: 4px;
  border: 1px solid #eaeae8;
  padding-top: 18px;
  padding-bottom: 8px;
  position: relative;
}

.select-thumbnail__item--active >>> .layout::after {
  content: "";
  position: absolute;
  inset: -1px;
  background: #0000001f;
  border: 1px solid #4cb5b8;
  pointer-events: none;
  border-radius: 4px;
  z-index: 1;
}

.thumb {
  height: 200px;
  display: flex;
  justify-content: center; /* 中央に置く */
  align-items: center;
}

.croper,
.croper-mini {
  max-height: 200px;
  overflow: hidden;
  border-radius: 1px;
  box-shadow: 1px 2px 4px 0px #00000014;
}

.croper-mini {
  max-height: 48px;
}

.croper img,
.croper-mini img {
  vertical-align: middle;
  width: auto;
  max-height: 200px;
}

.thumb-mini {
  max-width: 70px;
  height: 48px;
  display: flex;
  justify-content: center; /* 中央に置く */
  align-items: center;
  padding: 0 4px;
}

.subtext {
  font-size: 12px;
  line-height: 140%;
}

.cover-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cover-info li {
  /* 園・施設名、名前の半角スペースを入力通りに表示するため */
  white-space: pre-wrap;
}

.cover-spec {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.align-right {
  align-self: flex-end;
}

.attention-text {
  font-size: 12px;
  line-height: 150%;
  color: #fa6053;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dialog-card {
  padding: 20px;
  text-align: center;
}

.dialog-actions {
  gap: 16px;
}
</style>
